import {
	AlertCircle,
	Archive,
	CheckCircle,
	Clock,
	Download,
	ExternalLink,
	Eye,
	FileText,
	Plus,
	Search,
	Trash2,
	Wallet,
	X,
	XCircle,
} from "lucide-react";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../components/LoadingSpinner";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../components/ui/select";
import { useAuth } from "../context/AuthContext";
import { useWallets } from "../context/WalletContext";
import { useKYBCreate, useKYBDelete, useKYBFormList } from "../hooks";
import { useAsyncJob } from "../hooks/useAsyncJob";
import useConfirmDialog from "../hooks/useConfirmDialog";
import { getKybBadgeVariant, getKybStatusLabel } from "../lib/statusBadge";
import { cn } from "../lib/utils";
import { notifications } from "../utils/notifications";

const FILTER_TABS = [
	{ value: "all", label: "All" },
	{ value: "draft", label: "Drafts" },
	{ value: "submitted", label: "Submitted" },
	{ value: "under_review", label: "Under Review" },
	{ value: "approved", label: "Approved" },
	{ value: "rejected", label: "Rejected" },
];

/** Completeness bucket predicate for the client-side facet. */
function inCompletenessBucket(pct: number, bucket: string): boolean {
	switch (bucket) {
		case "empty":
			return pct === 0;
		case "low":
			return pct > 0 && pct <= 50;
		case "high":
			return pct > 50 && pct < 100;
		case "complete":
			return pct >= 100;
		default:
			return true;
	}
}

type HealthKey = "duplicate" | "orphan" | "stale" | "incomplete";
type HealthFlags = Record<HealthKey, boolean>;

const HEALTH_FILTERS: { key: HealthKey; label: string }[] = [
	{ key: "duplicate", label: "Duplicates" },
	{ key: "orphan", label: "Orphan pointer" },
	{ key: "incomplete", label: "Incomplete-submitted" },
	{ key: "stale", label: "Stale draft >30d" },
];
const HEALTH_LABEL: Record<HealthKey, string> = {
	duplicate: "Duplicate",
	orphan: "Orphan pointer",
	incomplete: "Incomplete",
	stale: "Stale draft",
};

const STALE_DRAFT_MS = 30 * 24 * 60 * 60 * 1000;

const KYCList: React.FC = () => {
	const navigate = useNavigate();
	const {
		isAdmin,
		isAuthenticated,
		loading: authLoading,
		hasAdminAccess,
	} = useAuth();
	const { confirm } = useConfirmDialog();
	const { wallets, getWallet, updateWallet } = useWallets();
	const [filterStatus, setFilterStatus] = useState<string>("all");
	const [page, setPage] = useState(1);
	// Search is server-side (matches business name + DBA across every page),
	// debounced so we don't fire a query on every keystroke. Facets below
	// refine the returned page client-side.
	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [countryFilter, setCountryFilter] = useState("all");
	const [entityFilter, setEntityFilter] = useState("all");
	const [walletTypeFilter, setWalletTypeFilter] = useState("all");
	const [completenessFilter, setCompletenessFilter] = useState("all");
	// Data-health triage lens (single-select). Computed client-side from the
	// loaded page + wallets context — cross-page detection (esp. duplicates) is
	// a follow-up that needs a server-side health aggregator.
	const [healthFilter, setHealthFilter] = useState<HealthKey | "none">("none");
	// Debounce the search box → server query. Reset to page 1 whenever the
	// effective term changes so results aren't hidden on a later page.
	useEffect(() => {
		const t = setTimeout(() => {
			setDebouncedSearch((prev) => {
				const next = search.trim();
				if (next !== prev) setPage(1);
				return next;
			});
		}, 300);
		return () => clearTimeout(t);
	}, [search]);
	// Track which form ID each job belongs to so one export doesn't disable all buttons
	const [pdfFormId, setPdfFormId] = useState<string | null>(null);
	const [bundleFormId, setBundleFormId] = useState<string | null>(null);
	const pdfJob = useAsyncJob({
		onComplete: (j) => {
			if (j.downloadUrl)
				window.open(j.downloadUrl, "_blank", "noopener,noreferrer");
			notifications.success("PDF ready");
			setPdfFormId(null);
		},
		onError: () => {
			notifications.error("PDF generation failed");
			setPdfFormId(null);
		},
	});
	const bundleJob = useAsyncJob({
		onComplete: (j) => {
			if (j.downloadUrl)
				window.open(j.downloadUrl, "_blank", "noopener,noreferrer");
			notifications.success("Attachments ready");
			setBundleFormId(null);
		},
		onError: () => {
			notifications.error("Bundling failed");
			setBundleFormId(null);
		},
	});

	const {
		data: kycForms,
		total: totalForms,
		totalPages,
		isLoading,
		error: kycError,
	} = useKYBFormList({
		enabled: !!isAuthenticated && !authLoading,
		status: filterStatus,
		search: debouncedSearch,
		page,
		limit: 50,
	});
	const createMutation = useKYBCreate();
	const deleteMutation = useKYBDelete();

	const getWalletName = (formId: string) => {
		const wallet = wallets.find(
			(w) => w.kyc_form_completed?.kyb_form_id === formId,
		);
		return wallet ? wallet.wallet_name : null;
	};

	const getWalletId = (
		form: { id: string; walletId?: string } & { kybFormId?: string },
	) => {
		if (form.walletId) return form.walletId;
		const wallet = wallets.find(
			(w) => w.kyc_form_completed?.kyb_form_id === form.id,
		);
		return wallet?.id;
	};

	const handleCreateNew = async () => {
		try {
			const newForm = await createMutation.mutateAsync(undefined);
			navigate(`/kyc/${newForm.id}`);
		} catch (err) {
			notifications.error(
				err instanceof Error ? err.message : "Failed to create KYB application",
			);
		}
	};

	const getStatusIcon = (status: string) => {
		switch (status) {
			case "draft":
			case "in-progress":
				return <Clock size={14} className="text-[#F59E0B]" />;
			case "submitted":
				return <FileText size={14} className="text-[#0EA5E9]" />;
			case "under_review":
				return (
					<AlertCircle size={14} className="text-(--color-muted-foreground)" />
				);
			case "approved":
				return <CheckCircle size={14} className="text-[#00E6C3]" />;
			case "rejected":
				return <XCircle size={14} className="text-(--color-accent)" />;
			default:
				return null;
		}
	};

	// Server handles status filtering + sorting (updated_at DESC); the facets
	// below refine the loaded page client-side.
	const facetOptions = useMemo(() => {
		const uniq = (vals: (string | undefined)[]) =>
			Array.from(
				new Set(vals.filter((v): v is string => !!v && v.trim() !== "")),
			).sort();
		return {
			countries: uniq(kycForms.map((f) => f.countryOfIncorporation)),
			entityTypes: uniq(kycForms.map((f) => f.entityType)),
			walletTypes: uniq(kycForms.map((f) => f.walletType)),
		};
	}, [kycForms]);

	// Data-health flags per loaded form, derived from the page + wallets context.
	// - duplicate: the form's wallet has >1 KYB form among the loaded rows
	// - orphan:    the wallet's pointer references a DIFFERENT form than this one
	// - stale:     draft/in-progress untouched for >30 days
	// - incomplete: status=submitted but <100% complete
	const healthByFormId = useMemo(() => {
		const now = Date.now();
		const walletIdOf = (form: (typeof kycForms)[number]): string | undefined =>
			form.walletId ??
			wallets.find((w) => w.kyc_form_completed?.kyb_form_id === form.id)?.id;

		const walletFormCounts = new Map<string, number>();
		for (const form of kycForms) {
			const wid = walletIdOf(form);
			if (wid) walletFormCounts.set(wid, (walletFormCounts.get(wid) ?? 0) + 1);
		}

		const map = new Map<string, HealthFlags>();
		for (const form of kycForms) {
			const wid = walletIdOf(form);
			const wallet = wid ? wallets.find((w) => w.id === wid) : undefined;
			const pointer = wallet?.kyc_form_completed?.kyb_form_id ?? null;
			const isDraft = form.status === "draft" || form.status === "in-progress";
			map.set(form.id, {
				duplicate: !!wid && (walletFormCounts.get(wid) ?? 0) > 1,
				orphan: !!pointer && pointer !== form.id,
				stale:
					isDraft && now - new Date(form.updatedAt).getTime() > STALE_DRAFT_MS,
				incomplete:
					form.status === "submitted" &&
					Math.round(form.percentComplete ?? 0) < 100,
			});
		}
		return map;
	}, [kycForms, wallets]);

	// Text search is handled server-side (see useKYBFormList); here we only
	// refine the returned page by the client-side facets + health lens.
	const sortedForms = useMemo(() => {
		return kycForms.filter((form) => {
			if (
				countryFilter !== "all" &&
				form.countryOfIncorporation !== countryFilter
			)
				return false;
			if (entityFilter !== "all" && form.entityType !== entityFilter)
				return false;
			if (walletTypeFilter !== "all" && form.walletType !== walletTypeFilter)
				return false;
			if (
				!inCompletenessBucket(
					Math.round(form.percentComplete ?? 0),
					completenessFilter,
				)
			)
				return false;
			if (
				healthFilter !== "none" &&
				!healthByFormId.get(form.id)?.[healthFilter]
			)
				return false;
			return true;
		});
	}, [
		kycForms,
		countryFilter,
		entityFilter,
		walletTypeFilter,
		completenessFilter,
		healthFilter,
		healthByFormId,
	]);

	const activeFacets = [
		countryFilter !== "all" && {
			key: "country",
			label: `Country: ${countryFilter}`,
			clear: () => setCountryFilter("all"),
		},
		entityFilter !== "all" && {
			key: "entity",
			label: `Entity: ${entityFilter}`,
			clear: () => setEntityFilter("all"),
		},
		walletTypeFilter !== "all" && {
			key: "wallet",
			label: `Wallet: ${walletTypeFilter}`,
			clear: () => setWalletTypeFilter("all"),
		},
		completenessFilter !== "all" && {
			key: "completeness",
			label: `Completeness: ${
				{
					empty: "0%",
					low: "1–50%",
					high: "51–99%",
					complete: "100%",
				}[completenessFilter] ?? completenessFilter
			}`,
			clear: () => setCompletenessFilter("all"),
		},
		healthFilter !== "none" && {
			key: "health",
			label: `Health: ${HEALTH_FILTERS.find((h) => h.key === healthFilter)?.label ?? healthFilter}`,
			clear: () => setHealthFilter("none"),
		},
	].filter((v): v is { key: string; label: string; clear: () => void } => !!v);

	const hasClientFilters = search.trim() !== "" || activeFacets.length > 0;
	const clearAllFilters = () => {
		setSearch("");
		setCountryFilter("all");
		setEntityFilter("all");
		setWalletTypeFilter("all");
		setCompletenessFilter("all");
		setHealthFilter("none");
	};

	return (
		<div className="min-h-[calc(100vh-56px)] bg-(--color-background) pt-20">
			<div className="mx-auto max-w-[1400px] px-4 py-6 md:px-6">
				{kycError && (
					<Alert variant="destructive" className="mb-6">
						<AlertDescription>
							<strong>Unable to load KYB forms:</strong>{" "}
							{kycError instanceof Error ? kycError.message : String(kycError)}
						</AlertDescription>
					</Alert>
				)}

				{/* Header */}
				<div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
					<div>
						<h1 className="font-bold text-(--color-foreground) text-3xl">
							KYB Applications
						</h1>
						<p className="mt-1 text-(--color-muted-foreground) text-sm">
							{isAdmin
								? "Manage business KYB submissions and reviews"
								: "View business KYB submissions"}
						</p>
					</div>
					{isAdmin && (
						<Button
							onClick={handleCreateNew}
							disabled={createMutation.isPending}
							className="shrink-0 rounded-xl bg-(--color-cta-primary) text-(--color-cta-primary-foreground) hover:bg-(--color-cta-primary-hover)"
						>
							<Plus size={16} />
							{createMutation.isPending ? "Creating..." : "New KYB Application"}
						</Button>
					)}
				</div>

				{/* Filter Tabs */}
				<div className="mb-5 flex overflow-x-auto border-(--color-border) border-b">
					{FILTER_TABS.map((tab) => (
						<button
							type="button"
							key={tab.value}
							className={cn(
								"-mb-px flex items-center gap-2 whitespace-nowrap border-b-2 px-5 py-3 font-medium text-sm transition-colors",
								filterStatus === tab.value
									? "border-(--color-accent) text-(--color-accent)"
									: "border-transparent text-(--color-muted-foreground) hover:text-(--color-foreground)",
							)}
							onClick={() => {
								setFilterStatus(tab.value);
								setPage(1);
							}}
						>
							{tab.label}
							<span
								className={cn(
									"inline-flex items-center justify-center rounded-full px-2 py-0.5 font-semibold text-xs",
									filterStatus === tab.value
										? "bg-(--color-primary)/15 text-(--color-primary)"
										: "bg-(--color-muted) text-(--color-muted-foreground)",
								)}
							>
								{filterStatus === tab.value ? totalForms : ""}
							</span>
						</button>
					))}
				</div>

				{/* Search + facets */}
				<div className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
					<div className="relative min-w-0 flex-1 sm:max-w-md">
						<Search
							size={16}
							className="absolute top-1/2 left-3 -translate-y-1/2 text-(--color-muted-foreground)"
						/>
						<Input
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder="Search business name or DBA…"
							className="rounded-xl pl-9"
							aria-label="Search KYB applications by business name or DBA"
						/>
						{search && (
							<button
								type="button"
								onClick={() => setSearch("")}
								aria-label="Clear search"
								className="absolute top-1/2 right-2 -translate-y-1/2 rounded-md p-1 text-(--color-muted-foreground) hover:text-(--color-foreground)"
							>
								<X size={14} />
							</button>
						)}
					</div>

					<Select value={countryFilter} onValueChange={setCountryFilter}>
						<SelectTrigger className="w-auto min-w-[8rem] rounded-xl">
							<SelectValue placeholder="Country" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All countries</SelectItem>
							{facetOptions.countries.map((c) => (
								<SelectItem key={c} value={c}>
									{c}
								</SelectItem>
							))}
						</SelectContent>
					</Select>

					<Select value={entityFilter} onValueChange={setEntityFilter}>
						<SelectTrigger className="w-auto min-w-[8rem] rounded-xl">
							<SelectValue placeholder="Entity type" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All entity types</SelectItem>
							{facetOptions.entityTypes.map((t) => (
								<SelectItem key={t} value={t}>
									{t}
								</SelectItem>
							))}
						</SelectContent>
					</Select>

					<Select value={walletTypeFilter} onValueChange={setWalletTypeFilter}>
						<SelectTrigger className="w-auto min-w-[8rem] rounded-xl">
							<SelectValue placeholder="Wallet type" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All wallet types</SelectItem>
							{facetOptions.walletTypes.map((t) => (
								<SelectItem key={t} value={t}>
									{t}
								</SelectItem>
							))}
						</SelectContent>
					</Select>

					<Select
						value={completenessFilter}
						onValueChange={setCompletenessFilter}
					>
						<SelectTrigger className="w-auto min-w-[8rem] rounded-xl">
							<SelectValue placeholder="Completeness" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">Any completeness</SelectItem>
							<SelectItem value="empty">0% (empty)</SelectItem>
							<SelectItem value="low">1–50%</SelectItem>
							<SelectItem value="high">51–99%</SelectItem>
							<SelectItem value="complete">100%</SelectItem>
						</SelectContent>
					</Select>
				</div>

				{/* Data-health triage filters (single-select lens) */}
				<div className="mb-4 flex flex-wrap items-center gap-2">
					<span className="mr-1 font-semibold text-(--color-muted-foreground) text-xs uppercase tracking-wide">
						Data health
					</span>
					{HEALTH_FILTERS.map(({ key, label }) => {
						const active = healthFilter === key;
						const count = kycForms.filter(
							(f) => healthByFormId.get(f.id)?.[key],
						).length;
						return (
							<button
								type="button"
								key={key}
								onClick={() =>
									setHealthFilter((cur) => (cur === key ? "none" : key))
								}
								className={cn(
									"inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 font-medium text-sm transition-colors",
									active
										? "border-[#F59E0B] bg-[#F59E0B]/15 text-[#F59E0B]"
										: "border-[#F59E0B]/30 bg-[#F59E0B]/5 text-(--color-foreground) hover:bg-[#F59E0B]/10",
								)}
							>
								<span className="size-1.5 rounded-full bg-[#F59E0B]" />
								{label}
								{count > 0 && (
									<span className="text-(--color-muted-foreground) text-xs">
										{count}
									</span>
								)}
							</button>
						);
					})}
				</div>

				{/* Active filter chips */}
				{hasClientFilters && (
					<div className="mb-5 flex flex-wrap items-center gap-2">
						{search.trim() && (
							<span className="inline-flex items-center gap-1.5 rounded-full bg-(--color-accent-muted) px-3 py-1 font-medium text-(--color-accent) text-xs">
								“{search.trim()}”
								<button
									type="button"
									onClick={() => setSearch("")}
									aria-label="Clear search filter"
								>
									<X size={12} />
								</button>
							</span>
						)}
						{activeFacets.map((f) => (
							<span
								key={f.key}
								className="inline-flex items-center gap-1.5 rounded-full bg-(--color-accent-muted) px-3 py-1 font-medium text-(--color-accent) text-xs"
							>
								{f.label}
								<button
									type="button"
									onClick={f.clear}
									aria-label={`Clear ${f.label}`}
								>
									<X size={12} />
								</button>
							</span>
						))}
						<button
							type="button"
							onClick={clearAllFilters}
							className="text-(--color-muted-foreground) text-xs underline hover:text-(--color-foreground)"
						>
							Clear all
						</button>
						<span className="ml-auto text-(--color-muted-foreground) text-xs">
							<b className="text-(--color-foreground)">{sortedForms.length}</b>{" "}
							on this page
							{debouncedSearch
								? ` · ${totalForms} match${totalForms === 1 ? "" : "es"}`
								: ""}
						</span>
					</div>
				)}

				{/* Content */}
				{isLoading ? (
					<div className="flex flex-col items-center justify-center py-20 text-center">
						<LoadingSpinner size="md" message="Loading KYB applications..." />
					</div>
				) : sortedForms.length === 0 && hasClientFilters ? (
					<div className="flex flex-col items-center justify-center py-20 text-center">
						<Search
							size={64}
							className="mb-4 text-(--color-muted-foreground) opacity-30"
						/>
						<h3 className="mb-2 font-semibold text-(--color-foreground) text-xl">
							No matches on this page
						</h3>
						<p className="text-(--color-muted-foreground) text-sm">
							No KYB applications on this page match your filters.
							{totalPages > 1 ? " Try another page or clear filters." : ""}
						</p>
						<Button
							variant="outline"
							onClick={clearAllFilters}
							className="mt-4 rounded-xl border-(--color-border) hover:bg-(--color-muted)"
						>
							<X size={16} />
							Clear filters
						</Button>
					</div>
				) : sortedForms.length === 0 ? (
					<div className="flex flex-col items-center justify-center py-20 text-center">
						<FileText
							size={64}
							className="mb-4 text-(--color-muted-foreground) opacity-30"
						/>
						<h3 className="mb-2 font-semibold text-(--color-foreground) text-xl">
							No KYB Applications
						</h3>
						<p className="text-(--color-muted-foreground) text-sm">
							Get started by creating a new KYB application
						</p>
						<Button
							onClick={handleCreateNew}
							disabled={createMutation.isPending}
							className="mt-4 rounded-xl bg-(--color-cta-primary) text-(--color-cta-primary-foreground) hover:bg-(--color-cta-primary-hover)"
						>
							<Plus size={16} />
							{createMutation.isPending ? "Creating..." : "New Application"}
						</Button>
					</div>
				) : (
					<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
						{sortedForms.map((form) => (
							// biome-ignore lint/a11y/useSemanticElements: Card needs to be a div for styling
							<Card
								key={form.id}
								className="group cursor-pointer rounded-xl border-(--color-border) bg-(--color-card) p-5 transition-all duration-200 hover:-translate-y-0.5 hover:border-(--color-accent) hover:shadow-lg"
								role="button"
								tabIndex={0}
								onClick={() => navigate(`/kyc/${form.id}`)}
								onKeyDown={(e) => {
									if (e.key === "Enter" || e.key === " ") {
										e.preventDefault();
										navigate(`/kyc/${form.id}`);
									}
								}}
							>
								{/* Card Header */}
								<div className="mb-4 flex items-start justify-between gap-4">
									<div className="min-w-0">
										<h3 className="mb-1 break-words font-semibold text-(--color-foreground) text-lg">
											{form.businessLegalName || "Untitled Application"}
										</h3>
										{form.dba && (
											<p className="text-(--color-muted-foreground) text-sm">
												DBA: {form.dba}
											</p>
										)}
										{getWalletName(form.id) && (
											<div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-(--color-accent-muted) px-2.5 py-1 font-semibold text-(--color-accent) text-xs">
												<Wallet size={12} />
												<span>{getWalletName(form.id)}</span>
											</div>
										)}
									</div>
									<div className="flex flex-wrap items-center justify-end gap-2">
										<Badge
											variant={getKybBadgeVariant(form.status)}
											className="flex items-center gap-1.5 whitespace-nowrap"
										>
											{getStatusIcon(form.status)}
											{getKybStatusLabel(form.status)}
										</Badge>
										{form.reviewerComments &&
											form.reviewerComments.length > 0 && (
												<span className="inline-block rounded-full bg-[#F59E0B]/10 px-2.5 py-1 font-semibold text-[#F59E0B] text-xs">
													{form.reviewerComments.length} comment
													{form.reviewerComments.length > 1 ? "s" : ""}
												</span>
											)}
									</div>
								</div>

								{/* Data-health flags */}
								{(() => {
									const h = healthByFormId.get(form.id);
									if (!h) return null;
									const flags: { key: HealthKey; cls: string }[] = [];
									if (h.duplicate)
										flags.push({
											key: "duplicate",
											cls: "bg-[#A78BFA]/15 text-[#A78BFA]",
										});
									if (h.orphan)
										flags.push({
											key: "orphan",
											cls: "bg-[#F59E0B]/15 text-[#F59E0B]",
										});
									if (h.incomplete)
										flags.push({
											key: "incomplete",
											cls: "bg-[#FB7185]/15 text-[#FB7185]",
										});
									if (h.stale)
										flags.push({
											key: "stale",
											cls: "bg-[#0EA5E9]/15 text-[#0EA5E9]",
										});
									if (flags.length === 0) return null;
									return (
										<div className="mb-3 flex flex-wrap gap-1.5">
											{flags.map((f) => (
												<span
													key={f.key}
													className={cn(
														"inline-flex items-center gap-1 rounded-md px-2 py-0.5 font-semibold text-xs",
														f.cls,
													)}
												>
													<AlertCircle size={11} />
													{HEALTH_LABEL[f.key]}
												</span>
											))}
										</div>
									);
								})()}

								{/* Info rows */}
								<div className="mb-4 rounded-xl bg-(--color-muted) p-4">
									{[
										{
											label: "Entity Type",
											value: form.entityType || "Not specified",
										},
										{
											label: "Country",
											value: form.countryOfIncorporation || "Not specified",
										},
										{ label: "Wallet Type", value: form.walletType },
									].map(({ label, value }) => (
										<div
											key={label}
											className="flex justify-between py-1 text-sm last:pb-0"
										>
											<span className="font-medium text-(--color-muted-foreground)">
												{label}:
											</span>
											<span className="text-(--color-foreground)">{value}</span>
										</div>
									))}
								</div>

								{/* Progress */}
								{(form.status === "draft" || form.status === "in-progress") && (
									<div className="mb-4">
										<Progress
											value={Math.round(form.percentComplete ?? 0)}
											className="mb-1 h-1.5"
										/>
										<span className="text-(--color-muted-foreground) text-xs">
											{Math.round(form.percentComplete ?? 0)}% Complete
										</span>
									</div>
								)}

								{/* Footer */}
								<div className="flex flex-col gap-3 border-(--color-border) border-t pt-4">
									<div className="flex flex-col gap-1">
										<span className="text-(--color-muted-foreground) text-xs">
											Last updated:{" "}
											{new Date(form.updatedAt).toLocaleDateString()}
										</span>
										{form.submittedAt && (
											<span className="text-(--color-muted-foreground) text-xs">
												Submitted:{" "}
												{new Date(form.submittedAt).toLocaleDateString()}
											</span>
										)}
									</div>
									<div className="flex flex-wrap gap-2">
										<Button
											size="sm"
											onClick={(e) => {
												e.stopPropagation();
												navigate(`/kyc/${form.id}`);
											}}
											className="rounded-xl bg-(--color-cta-primary) text-(--color-cta-primary-foreground) hover:bg-(--color-cta-primary-hover)"
										>
											<Eye size={13} />
											View
										</Button>
										<Button
											variant="outline"
											size="sm"
											disabled={!getWalletId(form)}
											title={
												getWalletId(form)
													? "Go to linked wallet"
													: "No wallet linked"
											}
											onClick={(e) => {
												e.stopPropagation();
												const walletId = getWalletId(form);
												if (walletId) navigate(`/wallet/${walletId}`);
											}}
											className="rounded-xl border-(--color-border) hover:bg-(--color-muted)"
										>
											<ExternalLink size={13} />
											Go to Wallet
										</Button>
										{isAdmin &&
											(form.status === "submitted" ||
												form.status === "approved") && (
												<>
													<Button
														variant="outline"
														size="sm"
														disabled={
															pdfFormId === form.id &&
															(pdfJob.job.status === "pending" ||
																pdfJob.job.status === "processing")
														}
														title="Download PDF summary"
														onClick={(e) => {
															e.stopPropagation();
															setPdfFormId(form.id);
															pdfJob.start(
																`/api/kyb-forms/${form.id}/export/pdf`,
															);
														}}
														className="rounded-xl border-(--color-border) hover:bg-(--color-muted)"
													>
														{pdfFormId === form.id &&
														(pdfJob.job.status === "pending" ||
															pdfJob.job.status === "processing") ? (
															<LoadingSpinner size="sm" />
														) : (
															<Download size={13} />
														)}
														PDF
													</Button>
													<Button
														variant="outline"
														size="sm"
														disabled={
															bundleFormId === form.id &&
															(bundleJob.job.status === "pending" ||
																bundleJob.job.status === "processing")
														}
														title="Download attachments ZIP"
														onClick={(e) => {
															e.stopPropagation();
															setBundleFormId(form.id);
															bundleJob.start(
																`/api/kyb-forms/${form.id}/export/attachments`,
															);
														}}
														className="rounded-xl border-(--color-border) hover:bg-(--color-muted)"
													>
														{bundleFormId === form.id &&
														(bundleJob.job.status === "pending" ||
															bundleJob.job.status === "processing") ? (
															<LoadingSpinner size="sm" />
														) : (
															<Archive size={13} />
														)}
														Attachments
													</Button>
												</>
											)}
										{isAdmin &&
											(form.status === "draft" ||
												form.status === "in-progress") && (
												<Button
													variant="destructive"
													size="sm"
													onClick={async (e) => {
														e.stopPropagation();
														const ok = await confirm({
															title: "Delete Draft",
															message:
																"Delete this draft KYB application? This cannot be undone.",
															confirmLabel: "Delete",
															variant: "danger",
														});
														if (!ok) return;
														try {
															await deleteMutation.mutateAsync(form.id);
															const walletId =
																form.walletId ?? getWalletId(form);
															if (walletId) {
																const wallet = getWallet(walletId);
																if (wallet?.kyc_form_completed?.kyb_form_id) {
																	await updateWallet(walletId, {
																		kyc_form_completed: {
																			...wallet.kyc_form_completed,
																			kyb_form_id: undefined,
																			kyb_progress: undefined,
																		},
																	});
																}
															}
														} catch (err) {
															notifications.error(
																err instanceof Error
																	? err.message
																	: "Failed to delete",
															);
														}
													}}
													title="Delete draft"
													className="rounded-xl bg-(--color-cta-primary) text-(--color-cta-primary-foreground) hover:bg-(--color-cta-primary-hover)"
												>
													<Trash2 size={13} />
													Delete
												</Button>
											)}
									</div>
								</div>
							</Card>
						))}
					</div>
				)}

				{/* Pagination */}
				{totalPages > 1 && (
					<div className="mt-6 flex items-center justify-center gap-4">
						<Button
							variant="outline"
							size="sm"
							disabled={page <= 1}
							onClick={() => setPage((p) => Math.max(1, p - 1))}
							className="rounded-xl"
						>
							Previous
						</Button>
						<span className="text-(--color-muted-foreground) text-sm">
							Page {page} of {totalPages}
						</span>
						<Button
							variant="outline"
							size="sm"
							disabled={page >= totalPages}
							onClick={() => setPage((p) => p + 1)}
							className="rounded-xl"
						>
							Next
						</Button>
					</div>
				)}
			</div>
		</div>
	);
};

export default KYCList;
